/**
 * Error Page - UI Component
 *
 * Root error boundary page for route rendering errors.
 *
 * @layer pages
 * @slice error
 * @segment ui
 * @added DOC-0099 (2025-12-26)
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Heading, Stack, Text } from '@shared/ui';

interface ErrorPageProps {
  /** Error that occurred */
  error: Error;
  /** Current locale for navigation */
  locale: string;
}

export function ErrorPage({ error, locale }: ErrorPageProps) {
  const { t } = useTranslation();
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 text-center">
          <Stack gap="lg" className="items-center">
            <Text as="div" className="text-6xl">
              ⚠️
            </Text>
            <div className="space-y-2">
              <Heading as="h1" size="2xl">
                {t('errorPage.title')}
              </Heading>
              <Text color="muted">{t('errorPage.description')}</Text>
            </div>
            {import.meta.env.DEV && (
              <div className="w-full rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-left">
                <Text size="sm" className="mb-2 font-medium text-destructive">
                  {t('errorPage.detailsTitle')}
                </Text>
                <pre className="overflow-auto text-xs text-destructive/80">{error.message}</pre>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-3">
              <Button type="button" onClick={handleReload}>
                {t('errorPage.retry')}
              </Button>
              <Button asChild variant="outline">
                <Link to="/$locale" params={{ locale }}>
                  {t('errorPage.goHome')}
                </Link>
              </Button>
            </div>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}
